import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import {

    switchMap, filter, map, catchError, tap, concatMap, first, takeWhile,
} from 'rxjs/operators';

import { CardAdjustments } from '../components/methods/payment-methods.models';
import { PaymentMethodFilterService } from '../components/methods/payment-methods.service';
import { CheckoutPaymentService } from '../services/checkout-payment.service';

import * as PaymentActions from './payment.actions';

import { getFilteredPaymentMethods, getPaymentReady, getSelectedDiscount, getSelectedPaymentMethod, SetSelectedPaymentMethod } from '.';

import { ApiCardAdjustmentsPayload } from '@api/_api/api-order/api-order.models';
import { OrderService } from '@api/order/order.service';
import { AppIdEnum } from '@core/domain/models/config.model';
import { getStoreAndLanguage, getCountryCode, getCmsConfigValue, getXConfigValue } from '@core/infrastructure/state/core.selectors';
import { PaymentMethod, DiscountDetails, SelectedDiscount } from '@features/payment';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { ErrorInfo } from '@shared/errors/models/error.models';
import { ShowError } from '@shared/errors/store/errors.actions';
import { SetActiveButton, SetInactiveButton, SetPendingStatus } from '@shared/navigation-button/store/navigation-button.actions';
import { LoadPurchaseAttemptSuccess } from '@shared/purchase/store/purchase.actions';
import { SetShopCartCoreShippingCosts } from '@shared/shopcart-core/infrastructure/state/shopcart-core.actions';
import { getShopCartCoreId, getShopCartCoreTotalOrder } from '@shared/shopcart-core/infrastructure/state/shopcart-core.selectors';

@Injectable()
export class PaymentEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly checkoutPaymentService: CheckoutPaymentService,
        private readonly orderService: OrderService,
        private readonly translateService: TranslateService,
        private readonly paymentMethodFilterService: PaymentMethodFilterService,
    ) { }

    /* Se utiliza la librería inditex para añadir giftcard/s como forma de pago porque usamos su
        función "submitPayment" para hacer el asynccheckout. La librería las guarda en una variable
        interna para añadirlas a la request cuando se llame a ese método. Si pasaramos a hacer
        la request "asynccheckout" manualmente se puede cambiar este valor a false */
    private readonly USE_INDITEX_TO_MANAGE_GIFTCARDS = true;

    public loadPaymentMethods$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PaymentActions.LoadPaymentMethods),
            concatLatestFrom(() => this.store.select(routerPublicQueries.getQueryParamsFromRouter)),
            switchMap(([_, params ]) => {
                if (params.orderId) {
                    return of([params.orderId, true] as [string, boolean]);
                }

                return this.store.select(getShopCartCoreId).pipe(

                    map((storedOrderId) => ([storedOrderId?.toString(), false] as [string, boolean])),

                    takeWhile(([storedOrderId]) => !storedOrderId && storedOrderId !== '', true),
                );
            }),
            filter(([orderId]) => {
                return !!orderId;
            }),
            concatLatestFrom(() => [
                this.store.select(getStoreAndLanguage),
                this.store.select(getResolution),
            ]),
            // TODO ECOMDUTI-21029 Si shopCart.id es -1 si el carro está vacío. ¿continuar o filtrar?
            switchMap(([[orderId, repay], { storeId, languageId }, resolution]) => {
                const appId = resolution === Resolutions.MOBILE ? AppIdEnum.mobile : undefined;

                return combineLatest([
                    this.orderService.isApplePaySupported(),
                    this.orderService.paymentMethods(storeId, languageId, orderId, appId, repay),
                ]).pipe(
                    map(([isApplePaySupported, values]) => {
                        let methods = values;
                        if (methods) {
                            if (!isApplePaySupported) {
                                methods = methods.filter((method) => method.kind !== 'applePay');
                            }
                            return PaymentActions.LoadPaymentMethodsSuccess({ values: methods });
                        }
                    }),
                    catchError((error: unknown) => {
                        console.error('[LoadPaymentMethods]', error);
                        return of(PaymentActions.LoadPaymentMethodsError({ error }));
                    }),
                );
            }),
        );
    });

    public getFilteredPaymentMethods$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PaymentActions.LoadPaymentMethodsSuccess),
            concatLatestFrom(() => [
                this.store.select(getSelectedPaymentMethod),
                this.store.select(getCountryCode),
                this.store.select(getSelectedDiscount),
                this.store.select(getCmsConfigValue('payment_order')),
                this.store.select(getCmsConfigValue('payment_hide')),
            ]),
            map(([
                paymentMethods,
                selectedPaymentMethod,
                countryCode,
                selectedDiscounts,
                paymentMethodsToReorder,
                paymentMethodsToHide,
            ]) => {
                let filteredMethods = paymentMethods?.values;
                // eslint-disable-next-line max-len
                const filterCodWithGiftcard = this.paymentMethodFilterService.paymentConfigs[countryCode]?.filterCodWithGiftcard;

                if (filteredMethods.length > 0 && paymentMethodsToHide?.length > 0) {
                    filteredMethods = this.paymentMethodFilterService.hide(filteredMethods, paymentMethodsToHide);
                }

                if (filteredMethods.length > 0 && paymentMethodsToReorder?.length > 0) {
                    filteredMethods = this.paymentMethodFilterService.reorder(filteredMethods, paymentMethodsToReorder);
                }

                if (selectedPaymentMethod?.kind === 'gift_card' && filterCodWithGiftcard) {
                    filteredMethods = this.paymentMethodFilterService.transform(filteredMethods, 'cod', 'kind', false);
                }

                const areThereAnyAddedDiscounts = selectedDiscounts.some((discount) => discount.discountMethod.type === 'Discount');

                if (areThereAnyAddedDiscounts) {
                    filteredMethods = this.paymentMethodFilterService.transform(filteredMethods, 'Discount', 'type', false);
                }

                return this.paymentMethodFilterService.transform(filteredMethods, 'giftCard', 'type', false);
            }),
            map((filteredMethods) => PaymentActions.LoadFilteredPaymentMethods({ paymentMethods: filteredMethods })),
        );
    });

    public setDefaultPaymentMethod$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PaymentActions.LoadFilteredPaymentMethods),
            concatLatestFrom(() =>
                this.store.select(getCmsConfigValue('payment_default_activa'))),
            filter(([_, paymentMethodDefault]) => !!paymentMethodDefault),
            concatLatestFrom(() => [
                this.store.select(getFilteredPaymentMethods),
                this.store.select(getCmsConfigValue('payment_check_default')),
            ]),
            map(([_, filteredMethods, deftMethod]) => {
                const defaultMethodSelect = filteredMethods.find(
                    (filteredMethod) => deftMethod.includes(filteredMethod?.code),
                );
                return SetSelectedPaymentMethod({ selectedPaymentMethod: defaultMethodSelect });
            }),
        );
    });

    public setUnboundPaymentMethod$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(PaymentActions.SetSelectedPaymentMethod),
                concatMap((action) =>
                    forkJoin([
                        this.store.select(getXConfigValue('ENABLED_UNBOUND_PAYMENTS')).pipe(first()),
                        this.store.select(getShopCartCoreId).pipe(first()),
                        this.store.select(getStoreAndLanguage).pipe(first()),
                        of(action.selectedPaymentMethod),
                    ])),
                filter(([enabled]) => !!enabled),
                concatMap(([_, orderId, { storeId, languageId }, selectedPaymentMethod]) =>

                    this.orderService.setUnboundPayment(storeId, languageId, orderId?.toString(), {
                        paymentData: [
                            {
                                paymentMethodType: selectedPaymentMethod.type,
                                paymentMethodKind: selectedPaymentMethod.kind,
                            },
                        ],
                    })),
                // map((order) => loadPurchaseAttemptSuccess({ values: order }))
                map((order) => PaymentActions.SetSelectedPaymentMethodSuccess({ values: order })),
                // eslint-disable-next-line rxjs/no-unsafe-catch
                catchError((error: unknown) => {
                    console.error('[setSelectedPaymentMethod]', error);

                    return of(PaymentActions.SetSelectedPaymentMethodError({ error }));
                }),
            );
        },
    );

    public setPurchaseAttemptValues$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(PaymentActions.SetSelectedPaymentMethodSuccess),
                map((action) => {
                    const purchaseAttempt = {
                        adjustment: action.values.adjustment,
                        billingAddressId: action.values.billingAddressId,
                        billingAddressIdString: action.values.billingAddressIdString,
                        delivery: null,
                        id: action.values.id,
                        isNoNexus: null,
                        isTaxIncluded: null,
                        items: action.values.items,
                        payment: action.values.payment,
                        personalizationId: action.values.personalizationId,
                        promotion: action.values.promotion,
                        shippingPrice: action.values.shippingPrice,
                        shippingTax: action.values.shippingTax,
                        shippingTaxArray: action.values.shippingTaxArray,
                        tax: action.values.tax,
                        taxArray: action.values.taxArray,
                        totalAdjustment: action.values.totalAdjustment,
                        totalOrder: action.values.totalOrder,
                        totalProduct: action.values.totalProduct,
                    };
                    return LoadPurchaseAttemptSuccess({ values: purchaseAttempt });
                }),
            );
        },
    );
    // eslint-disable-next-line jira-ticket-todo-comment/jira-ticket-todo-comment
    // TODO ECOMDUTI-????? Aqui hay un problema a la hora de activar el boton de navegar, se deberia activar cuando se selecciona un metodo de pago a través del SetSelectedPaymentMethodSuccess
    // Pero se está haciendo en el componente llamando a un dispatch, ya que hay un filter que solo en algunos casos lanza el SetSelectedPaymentMethodSuccess
    // Esté efecto debería lanzarse en el SetSelectedPaymentMethodSuccess no en el SetSelectedPaymentMethod una vez arreglado lo comentado

    public setActiveButton$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(PaymentActions.SetSelectedPaymentMethod),
                map(() => SetActiveButton()),
            );
        },
    );

    public setShopcartCartValues$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(PaymentActions.SetSelectedPaymentMethodSuccess),
                map((action) => SetShopCartCoreShippingCosts({
                    adjustment: action.values.adjustment,
                    shippingPrice: action.values.shippingPrice,
                    shippingTax: action.values.shippingTax,
                    totalOrder: action.values.totalOrder,
                    taxArray: action.values.taxArray,
                    tax: action.values.tax,
                })),
            );
        },
    );

    public loadPaymentMethodsError$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(PaymentActions.LoadPaymentMethodsError),
                tap(({ error }) => {
                    console.error('[LoadPaymentMethodsError]', error);
                }),
                concatLatestFrom(() => this.store.select(getCmsConfigValue('contact_info_error'))),
                map(([_, contactInfo]: [Action, string]) => {
                    const errorText = this.translateService.instant('dev.general.error.contact');
                    const errorInfo: ErrorInfo = {
                        title: 'dev.general.error.dialogTitle',
                        subtitle: `${errorText} ${contactInfo || ''}`,
                        onConfirm: () => { },
                    };
                    return ShowError({ error: errorInfo, kind: 'general' });
                }),
            );
        },
        { dispatch: false },
    );

    public setDiscountDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PaymentActions.SetDiscountDetails),
            concatLatestFrom(() => [
                this.store.select(getShopCartCoreId),
                this.store.select(getStoreAndLanguage),
                this.store.select(getSelectedDiscount),
                this.store.select(getShopCartCoreTotalOrder),
            ]),
            concatMap(([action, orderId, { storeId, languageId }, currentSelectedDiscount, totalOrder]) =>
                this.orderService.setCardAdjustments(
                    storeId,
                    orderId.toString(),
                    languageId,
                    this.getCardAdjustmentsPayloadObject(action.cardAdjustments),
                    this.USE_INDITEX_TO_MANAGE_GIFTCARDS,
                ).pipe(
                    map((discountDetails) => {
                        const selectedDiscount: SelectedDiscount[] = this.getNewSelectedDiscount(
                            discountDetails,
                            currentSelectedDiscount,
                            action,
                        );
                        const paymentReady: boolean = this.checkoutPaymentService.isDiscountBalanceEnough(
                            selectedDiscount,
                            totalOrder,
                        );

                        return PaymentActions.SetDiscountDetailsSuccess({ selectedDiscount, paymentReady });
                    }),
                    catchError((error: unknown) => of(PaymentActions.SetDiscountDetailsError({ error }))),
                )),
        );
    });

    public handleDiscountDetailsErrors$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(PaymentActions.SetDiscountDetailsError),
                map(({ error, type }) => {
                    console.error('[SetDiscountDetailsError$]', { error, type });
                    switch (error.key) {
                        case '_WARN_GIFT_CARD_INVALID': {
                            const errorInfo: ErrorInfo = {
                                title: 'dev.checkout.summary.invalidData',
                                subtitle: error.message,
                                onConfirm: () => { },
                            };

                            return ShowError({ error: errorInfo, kind: 'general' });
                        }
                        case '_INVALID_CAPTCHA': {
                            const errorInfo: ErrorInfo = {
                                subtitle: 'dev.forms.invalidCaptcha',
                                onConfirm: () => { },
                            };

                            return ShowError({ error: errorInfo, kind: 'general' });
                        }
                        case '_WARN_GIFT_CARD_WRONG_BRAND_COUNTRY': {
                            const errorInfo: ErrorInfo = {
                                subtitle: error.message,
                                onConfirm: () => { },
                            };

                            return ShowError({ error: errorInfo, kind: 'general' });
                        }
                        case 'GIFT_CARD_EXHAUSTED_ERROR': {
                            const errorInfo: ErrorInfo = {
                                subtitle: 'dev.checkout.giftcard.notBalance',
                                onConfirm: () => {},
                            };

                            return ShowError({ error: errorInfo, kind: 'general' });
                        }
                        default: {
                            const errorInfo: ErrorInfo = {
                                title: 'dev.general.error.dialogTitle',
                                subtitle: 'dev.general.error.contact',
                                onConfirm: () => { },
                            };

                            return ShowError({ error: errorInfo, kind: 'general' });
                        }
                    }
                }),
            );
        },
    );

    public setErrorStatus$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(PaymentActions.SetDiscountDetailsError),
                map(() => SetPendingStatus()),
            );
        },
    );

    public deselectPaymentMethod$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PaymentActions.ToggleDiscountMethod),
            concatLatestFrom(() => this.store.select(getSelectedDiscount)),
            filter(([action, currentSelectedDiscount]) => {
                // Deselect the selected payment method if all discounts are disabled and one of them is being activated.
                return action.enabled && currentSelectedDiscount.every(
                    (discount) => !discount.enabled,
                );
            }),
            map(() => SetSelectedPaymentMethod({ selectedPaymentMethod: null })),
        );
    });

    public toggleDiscountMethod$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PaymentActions.ToggleDiscountMethod),
            concatLatestFrom(() => [
                this.store.select(getSelectedDiscount),
                this.store.select(getShopCartCoreId),
                this.store.select(getStoreAndLanguage),
            ]),
            switchMap(([action, currentSelectedDiscount, orderId, { storeId, languageId }]) => {
                const payload: ApiCardAdjustmentsPayload = this.getCardAdjustmentsPayloadObject(
                    action.discount.discountDetails.values,
                );

                let discountDetails$: Observable<DiscountDetails>;

                if (action.enabled) {
                    discountDetails$ = this.orderService.setCardAdjustments(
                        storeId,

                        orderId.toString(),
                        languageId,
                        payload,
                        this.USE_INDITEX_TO_MANAGE_GIFTCARDS,
                    );
                } else {
                    const cardIndex = currentSelectedDiscount.findIndex(({ discountDetails }) => {
                        return discountDetails.values.number === action.discount.discountDetails.values.number;
                    });
                    discountDetails$ = this.orderService.unsetCardAdjustments(
                        storeId,

                        orderId.toString(),
                        languageId,
                        payload,
                        this.USE_INDITEX_TO_MANAGE_GIFTCARDS,
                        cardIndex,
                    );
                }

                return discountDetails$.pipe(
                    concatLatestFrom(() => [
                        this.store.select(getShopCartCoreTotalOrder),
                        this.store.select(getSelectedPaymentMethod),
                        this.store.select(getPaymentReady),
                    ]),
                    map(([ _, totalOrder, selectedPaymentMethod, currentPaymentReady ]) => {
                        const selectedDiscount: SelectedDiscount[] = this.toggleSelectedDiscount(
                            action.discount,
                            currentSelectedDiscount,
                            action.enabled,
                        );

                        const paymentReady: boolean = this.getIsPaymentReady(
                            selectedDiscount,
                            totalOrder,
                            selectedPaymentMethod,
                            currentPaymentReady,
                        );

                        return PaymentActions.ToggleDiscountMethodSuccess({ selectedDiscount, paymentReady });
                    }),
                    catchError((error: unknown) => {
                        console.error('[ToggleDiscountMethod]', error);
                        return of(PaymentActions.ToggleDiscountMethodError({ error }));
                    }),
                );
            }),
        );
    });

    public setButtonStatusOnToggleDiscount$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PaymentActions.ToggleDiscountMethodSuccess),
            map(({ paymentReady }) => {
                if (paymentReady) return SetActiveButton();
                return SetInactiveButton();
            }),
        );
    });

    public clearPaymentMethodOnToggleDiscount$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PaymentActions.ToggleDiscountMethodSuccess),
            filter((action) => action.selectedDiscount.length === 0),
            map(() => SetSelectedPaymentMethod({ selectedPaymentMethod: null })),
        );
    });

    private toggleSelectedDiscount(
        targetSelectedDiscount: SelectedDiscount,
        currentSelectedDiscount: SelectedDiscount[],
        _enabledDiscountMethod: boolean,
    ): SelectedDiscount[] {
        const newsSelectedDiscount: SelectedDiscount[] = currentSelectedDiscount.filter((discount: SelectedDiscount) => {
            return discount.discountDetails.values.number !== targetSelectedDiscount.discountDetails.values.number;
        });

        // .map((discount: SelectedDiscount) => {
        //     if (discount.discountDetails.values.number !== targetSelectedDiscount.discountDetails.values.number) {
        //         return discount;
        //     } else {
        //         return {
        //             discountMethod: targetSelectedDiscount.discountMethod,
        //             discountDetails: targetSelectedDiscount.discountDetails,
        //             enabled: enabledDiscountMethod
        //         };
        //     }
        // });

        return newsSelectedDiscount;
    }

    private getNewSelectedDiscount(
        discountDetails: DiscountDetails,
        currentSelectedDiscount: SelectedDiscount[],
        action: any,
    ): SelectedDiscount[] {
        const selectedDiscount: SelectedDiscount[] = [];

        // biome-ignore lint/complexity/noForEach: <explanation>
        currentSelectedDiscount.forEach((discount: SelectedDiscount) => {
            if (discount.discountDetails.values.number !== discountDetails.values.number) {
                selectedDiscount.push(discount);
            }
        });
        const newSelectedDiscount: SelectedDiscount = {
            discountMethod: action.discountMethod,
            discountDetails,
            enabled: true,
        };
        selectedDiscount.push(newSelectedDiscount);

        return selectedDiscount;
    }

    private getCardAdjustmentsPayloadObject(discount: CardAdjustments): ApiCardAdjustmentsPayload {
        const cardAdjustmentsPayload = {
            paymentData: [
                {
                    balance: 0,
                    cvv2: discount.cvv2,
                    number: discount.number,
                    paymentMethodKind: discount.paymentMethodKind,
                    paymentMethodType: discount.paymentMethodType,
                    variant: discount.variant,
                },
            ],
        };
        if (discount?.captchaId || discount?.captchaValue) {
            return {
                ...cardAdjustmentsPayload,
                captchaId: discount?.captchaId,
                captchaValue: discount?.captchaValue,
            };
        }
        return cardAdjustmentsPayload;
    }

    private getIsPaymentReady(
        selectedDiscount: SelectedDiscount[],
        totalOrder: number,
        selectedPaymentMethod: PaymentMethod,
        currentPaymentReady: boolean,
    ): boolean {
        let paymentReady: boolean = this.checkoutPaymentService.isDiscountBalanceEnough(
            selectedDiscount,
            totalOrder,
        );

        if (selectedPaymentMethod?.kind !== 'gift_card' && !paymentReady) {
            paymentReady = currentPaymentReady;
        }
        return paymentReady;
    }
}
